import { supabase } from "./supabase";

/**
 * Create a new benchmark run and add it to the global queue
 * @param {string} generatorId - ID of the generator to use
 * @param {Object} filters - Benchmark filters {subjects, grades, curriculum}
 * @param {string} userId - ID of the user creating the run
 * @param {string} datasetVersion - Dataset version to use (null for new dynamic runs)
 * @param {string} inceptbenchVersion - InceptBench evaluator version to use
 * @param {string} [curriculumVersion] - Optional curriculum standard version (e.g., "1.2")
 * @param {string} [sourceRunId] - Optional ID of run to reuse questions from (for re-evaluation)
 * @param {number} [configVersionId] - Optional config version ID from bm_config_versions
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const createRun = async (
  generatorId,
  filters,
  userId,
  datasetVersion,
  inceptbenchVersion,
  curriculumVersion = null,
  sourceRunId = null,
  configVersionId = null
) => {
  try {
    const runData = {
      generator_id: generatorId,
      user_id: userId,
      filters,
      dataset_version: datasetVersion,
      inceptbench_version: inceptbenchVersion,
      curriculum_version: curriculumVersion,
      status: "queued",
      queued_at: new Date().toISOString(),
      generation_progress: 0,
      evaluation_progress: 0,
      items_generated: 0,
      items_evaluated: 0,
    };

    // Add config_version_id if provided (for dynamic request generation)
    if (configVersionId) {
      runData.config_version_id = configVersionId;
    }

    // Add source_run_id if provided (for re-evaluation runs)
    if (sourceRunId) {
      runData.source_run_id = sourceRunId;
    }

    const { data, error } = await supabase
      .from("bm_runs")
      .insert([runData])
      .select()
      .single();

    if (error) {
      throw error;
    }

    // Link related runs atomically via RPC (updates all group members' arrays)
    if (sourceRunId) {
      const { error: linkError } = await supabase.rpc("link_related_run", {
        p_new_run_id: data.id,
        p_source_run_id: sourceRunId,
      });

      if (linkError) {
        console.error("Error linking related runs:", linkError);
      }
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error creating run:", error);
    return { data: null, error };
  }
};

/**
 * Fetch a single run by ID
 * @param {string} runId - Run ID
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const fetchRun = async (runId) => {
  try {
    const { data, error } = await supabase
      .from("bm_runs")
      .select("*, bm_generators(name, endpoint_url)")
      .eq("id", runId)
      .single();

    if (error) {
      throw error;
    }

    // Note: source_run_id is automatically included in the * select
    return { data, error: null };
  } catch (error) {
    console.error("Error fetching run:", error);
    return { data: null, error };
  }
};

/**
 * Fetch user's runs with optional status filter
 * @param {string} userId - User ID
 * @param {string} [status] - Optional status filter ('queued', 'generating', 'evaluating', 'completed', 'failed')
 * @param {Object} options - Query options
 * @param {boolean} options.includeRawData - Whether to include raw_* JSONB fields (default: false)
 * @param {boolean} options.includeArchived - Whether to include archived runs (default: false)
 * @returns {Promise<{data: Array, error: Object}>}
 */
export const fetchUserRuns = async (userId, status = null, options = {}) => {
  try {
    const { includeRawData = false, includeArchived = false } = options;

    // Select only necessary columns for list view (exclude large JSONB fields)
    const selectFields = includeRawData
      ? "*, bm_generators(name)"
      : `
        id,
        generator_id,
        user_id,
        filters,
        status,
        generation_progress,
        evaluation_progress,
        total_items,
        items_generated,
        items_evaluated,
        aggregate_score,
        pass_rate,
        created_at,
        started_at,
        completed_at,
        error_message,
        inceptbench_version,
        curriculum_version,
        is_archived,
        archived_at,
        variety_score,
        is_static,
        variety_status,
        bm_generators(name)
      `;

    let query = supabase
      .from("bm_runs")
      .select(selectFields)
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (status) {
      query = query.eq("status", status);
    }

    // Exclude archived runs by default
    if (!includeArchived) {
      query = query.eq("is_archived", false);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error fetching user runs:", error);
    return { data: null, error };
  }
};

/**
 * Fetch active (in-progress) runs for a user
 * @param {string} userId - User ID
 * @param {Object} options - Query options
 * @param {boolean} options.includeRawData - Whether to include raw_* JSONB fields (default: false)
 * @returns {Promise<{data: Array, error: Object}>}
 */
export const fetchActiveRuns = async (userId, options = {}) => {
  try {
    const { includeRawData = false } = options;

    // Select only necessary columns for list view (exclude large JSONB fields)
    const selectFields = includeRawData
      ? "*, bm_generators(name)"
      : `
        id,
        generator_id,
        user_id,
        filters,
        status,
        generation_progress,
        evaluation_progress,
        total_items,
        items_generated,
        items_evaluated,
        aggregate_score,
        pass_rate,
        created_at,
        started_at,
        completed_at,
        error_message,
        inceptbench_version,
        curriculum_version,
        bm_generators(name)
      `;

    const { data, error } = await supabase
      .from("bm_runs")
      .select(selectFields)
      .eq("user_id", userId)
      .eq("is_archived", false) // Exclude archived runs
      .in("status", ["queued", "generating", "evaluating"])
      .order("created_at", { ascending: false })
      .limit(1);

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error fetching active runs:", error);
    return { data: null, error };
  }
};

/**
 * Fetch this user's runs that are currently in the global queue or running.
 * Ordered by order_tag (the fair-queue tag assigned at enqueue time), with
 * queued_at as a stable tie-breaker.
 * @param {string} userId - User ID
 * @returns {Promise<{data: Array, error: Object}>}
 */
export const fetchQueueSessionRuns = async (userId) => {
  try {
    const { data, error } = await supabase
      .from("bm_runs")
      .select(
        `
        id,
        generator_id,
        filters,
        status,
        order_tag,
        queued_at,
        generation_progress,
        evaluation_progress,
        aggregate_score,
        pass_rate,
        completed_at,
        dataset_version,
        inceptbench_version,
        curriculum_version,
        bm_generators(name)
      `
      )
      .eq("user_id", userId)
      .eq("is_archived", false)
      .in("status", ["queued", "generating", "evaluating"])
      .order("order_tag", { ascending: true, nullsFirst: false })
      .order("queued_at", { ascending: true });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error fetching queue session runs:", error);
    return { data: null, error };
  }
};

/**
 * Delete a queued run (remove from queue)
 * @param {string} runId - Run ID to delete
 * @returns {Promise<{error: Object|null}>}
 */
export const deleteQueuedRun = async (runId) => {
  try {
    const { error } = await supabase
      .from("bm_runs")
      .delete()
      .eq("id", runId)
      .eq("status", "queued"); // Only allow deleting queued runs

    if (error) {
      throw error;
    }

    return { error: null };
  } catch (error) {
    console.error("Error deleting queued run:", error);
    return { error };
  }
};

/**
 * Fetch all completed and failed runs
 * @param {Object} options - Query options
 * @param {boolean} options.includeRawData - Whether to include raw_* JSONB fields (default: false)
 * @param {boolean} options.includeArchived - Whether to include archived runs (default: false)
 * @returns {Promise<{data: Array, error: Object}>}
 */
export const fetchCompletedRuns = async (options = {}) => {
  try {
    const { includeRawData = false, includeArchived = false } = options;

    // Select only necessary columns for list view (exclude large JSONB fields)
    const selectFields = includeRawData
      ? "*, bm_generators(name)"
      : `
        id,
        generator_id,
        user_id,
        filters,
        status,
        generation_progress,
        evaluation_progress,
        total_items,
        items_generated,
        items_evaluated,
        aggregate_score,
        pass_rate,
        created_at,
        started_at,
        completed_at,
        error_message,
        inceptbench_version,
        curriculum_version,
        is_archived,
        archived_at,
        variety_score,
        is_static,
        variety_status,
        bm_generators(name)
      `;

    let query = supabase
      .from("bm_runs")
      .select(selectFields)
      .in("status", ["completed", "failed"])
      .order("created_at", { ascending: false });

    // Exclude archived runs by default
    if (!includeArchived) {
      query = query.eq("is_archived", false);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error fetching completed runs:", error);
    return { data: null, error };
  }
};

/**
 * Fetch all runs regardless of status
 * Optimized to exclude large JSONB fields for list views
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of runs to fetch (default: 100)
 * @param {number} options.offset - Number of runs to skip for pagination (default: 0)
 * @param {boolean} options.includeRawData - Whether to include raw_* JSONB fields (default: false)
 * @param {boolean} options.includeArchived - Whether to include archived runs (default: false)
 * @returns {Promise<{data: Array, error: Object, count: number}>}
 */
export const fetchAllRuns = async (options = {}) => {
  try {
    const {
      limit = 100,
      offset = 0,
      includeRawData = false,
      includeArchived = false,
    } = options;

    // Select only necessary columns for list view (exclude large JSONB fields)
    const selectFields = includeRawData
      ? "*, bm_generators(name)"
      : `
        id,
        generator_id,
        user_id,
        filters,
        status,
        generation_progress,
        evaluation_progress,
        total_items,
        items_generated,
        items_evaluated,
        aggregate_score,
        pass_rate,
        created_at,
        started_at,
        completed_at,
        error_message,
        dataset_version,
        inceptbench_version,
        curriculum_version,
        is_archived,
        archived_at,
        variety_score,
        is_static,
        variety_status,
        bm_generators(name)
      `;

    let query = supabase
      .from("bm_runs")
      .select(selectFields, { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    // Exclude archived runs by default
    if (!includeArchived) {
      query = query.eq("is_archived", false);
    }

    const { data, error, count } = await query;

    if (error) {
      throw error;
    }

    return { data, error: null, count };
  } catch (error) {
    console.error("Error fetching all runs:", error);
    return { data: null, error, count: 0 };
  }
};

/**
 * Get total count of runs
 * @returns {Promise<{count: number, error: Object}>}
 */
export const fetchRunsCount = async () => {
  try {
    const { count, error } = await supabase
      .from("bm_runs")
      .select("*", { count: "exact", head: true });

    if (error) {
      throw error;
    }

    return { count: count || 0, error: null };
  } catch (error) {
    console.error("Error fetching runs count:", error);
    return { count: 0, error };
  }
};

/**
 * Fetch latest completed run for each generator
 * @param {Object} options - Query options
 * @param {boolean} options.includeRawData - Whether to include raw_* JSONB fields (default: false)
 * @returns {Promise<{data: Array, error: Object}>}
 */
export const fetchLatestRunsByGenerator = async (options = {}) => {
  try {
    const { includeRawData = false } = options;

    // Select only necessary columns for list view (exclude large JSONB fields)
    const selectFields = includeRawData
      ? "*, bm_generators(name)"
      : `
        id,
        generator_id,
        user_id,
        filters,
        status,
        generation_progress,
        evaluation_progress,
        total_items,
        items_generated,
        items_evaluated,
        aggregate_score,
        pass_rate,
        created_at,
        started_at,
        completed_at,
        error_message,
        inceptbench_version,
        curriculum_version,
        bm_generators(name)
      `;

    const { data, error } = await supabase
      .from("bm_runs")
      .select(selectFields)
      .eq("status", "completed")
      .eq("is_archived", false) // Exclude archived runs
      .order("created_at", { ascending: false });

    if (error) {
      throw error;
    }

    // Group by generator and get the latest for each
    const latestRuns = [];
    const seenGenerators = new Set();

    if (data) {
      for (const run of data) {
        if (run.generator_id && !seenGenerators.has(run.generator_id)) {
          seenGenerators.add(run.generator_id);
          latestRuns.push(run);
        }
      }
    }

    return { data: latestRuns, error: null };
  } catch (error) {
    console.error("Error fetching latest runs by generator:", error);
    return { data: null, error };
  }
};

const PASS_THRESHOLD = 0.85;

/**
 * Identify failed requests from a run
 * @param {Object} rawGenerationResults - Generation results from run
 * @param {Object} rawEvaluationResults - Evaluation results from run
 * @param {Object} rawRequests - Raw requests from run (to detect items never generated)
 * @returns {Object} Object with failedGeneration, failedEvaluation, and failedQuestions arrays
 */
export const identifyFailedRequests = (
  rawGenerationResults,
  rawEvaluationResults,
  rawRequests
) => {
  const failedGeneration = [];
  const failedEvaluation = [];
  const failedQuestions = [];

  // Find failed generation requests
  // This includes:
  // 1. Generation API errors (has error field or success === false)
  // 2. Empty/blank content (generation succeeded but content is empty)
  // 3. Items in raw_requests that were never generated at all
  if (rawGenerationResults && typeof rawGenerationResults === "object") {
    for (const [requestId, result] of Object.entries(rawGenerationResults)) {
      // Case 1: Generation API failed
      if (result && (result.success === false || result.error)) {
        failedGeneration.push({
          requestId,
          error: result.error,
          timestamp: result.timestamp,
        });
        continue;
      }

      // Case 2: Generation succeeded but content is empty
      // Check if there's an evaluation result marked as isEmpty
      const evalResult = rawEvaluationResults?.[requestId];
      if (evalResult && evalResult.isEmpty) {
        failedGeneration.push({
          requestId,
          error: "Generated empty/blank content",
          timestamp: result.timestamp,
        });
      }
    }
  }

  // Case 3: Items in raw_requests that were never generated
  if (rawRequests && typeof rawRequests === "object") {
    for (const requestId of Object.keys(rawRequests)) {
      if (!rawGenerationResults || !(requestId in rawGenerationResults)) {
        failedGeneration.push({
          requestId,
          error: "Never generated",
          timestamp: null,
        });
      }
    }
  }

  // Find failed evaluation requests (where generation succeeded but evaluation failed)
  // AND low-scoring questions (evaluated successfully but below pass threshold)
  if (rawGenerationResults && typeof rawGenerationResults === "object") {
    for (const [requestId, genResult] of Object.entries(rawGenerationResults)) {
      // Only check items where generation succeeded (no generation error)
      if (genResult && !genResult.error && genResult.success !== false) {
        const evalResult = rawEvaluationResults?.[requestId];

        // Skip if this is empty content (already counted as generation error)
        if (evalResult && evalResult.isEmpty) {
          continue;
        }

        // Case 1: No evaluation result at all (generated but not evaluated)
        if (!evalResult) {
          failedEvaluation.push({
            requestId,
            error: "Generated but not evaluated",
            timestamp: genResult.timestamp,
          });
          continue;
        }

        // Case 2: Evaluation has explicit error
        if (evalResult.error) {
          failedEvaluation.push({
            requestId,
            error: evalResult.error,
            timestamp: evalResult.timestamp,
          });
          continue;
        }

        // Case 3: Evaluated successfully but scored below the pass threshold.
        // Excludes generation failures, schema validation failures, and empty
        // content which are already handled above.
        if (
          !evalResult.isGenerationFailure &&
          !evalResult.isSchemaValidationFailure &&
          !evalResult.passed &&
          typeof evalResult.score === "number" &&
          evalResult.score < PASS_THRESHOLD
        ) {
          failedQuestions.push({
            requestId,
            score: evalResult.score,
            timestamp: evalResult.timestamp,
          });
        }
      }
    }
  }

  return {
    failedGeneration,
    failedEvaluation,
    failedQuestions,
    totalFailed:
      failedGeneration.length +
      failedEvaluation.length +
      failedQuestions.length,
  };
};

/**
 * Trigger a re-run for a benchmark run
 * Uses Supabase Edge Function to trigger Cloud Run Job (same as normal runs)
 * @param {string} runId - Run ID to re-run
 * @param {Array<string>} requestIds - Optional array of specific request IDs to re-run
 * @returns {Promise<{success: boolean, error: Object, message: string}>}
 */
export const triggerRerun = async (runId, requestIds = null) => {
  try {
    // Get current session for auth token
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error("No active session. Please log in again.");
    }

    // Store selected request IDs in the database (instead of passing via env vars
    // to Cloud Run, which has a 32KB env var size limit that large reruns exceed)
    // Also update status to "queued" for instant feedback to the user.
    // Status guard: only re-queue from a terminal state so a UI race can't
    // reset an in-flight run and cause a duplicate Cloud Run job.
    const { data: updatedRows, error: updateError } = await supabase
      .from("bm_runs")
      .update({
        status: "queued",
        is_rerun: true,
        generation_progress: 0,
        evaluation_progress: 0,
        queued_at: new Date().toISOString(),
        rerun_request_ids:
          requestIds && requestIds.length > 0 ? requestIds : null,
      })
      .eq("id", runId)
      .in("status", ["completed", "failed", "cancelled"])
      .select("id");

    if (updateError) {
      console.error("Error updating status to queued:", updateError);
      // Don't throw - continue with re-run even if status update fails
    } else if (!updatedRows || updatedRows.length === 0) {
      return {
        success: false,
        error: {
          message:
            "Run is not in a re-triggerable state. It must be completed, failed, or cancelled.",
        },
      };
    }

    // Call Supabase Edge Function (same pattern as normal runs)
    // Note: request IDs are stored in DB, not passed here (avoids Cloud Run env var size limit)
    const supabaseUrl = supabase.supabaseUrl;
    const response = await fetch(`${supabaseUrl}/functions/v1/trigger-rerun`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        runId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      // Don't revert: the row is already in 'queued' and the process-queue
      // cron tick (~30s) will start it even if this poke failed. Reverting
      // here would (a) mislabel rows that were 'failed'/'cancelled' as
      // 'completed' and (b) race with the scheduler if it already flipped
      // the row to 'generating'.
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();

    return {
      success: true,
      error: null,
      message: "Re-run started successfully on Cloud Run",
      data: data,
    };
  } catch (error) {
    console.error("Error triggering re-run:", error);
    return {
      success: false,
      error: {
        message: error.message || "Failed to trigger re-run",
      },
    };
  }
};

/**
 * Cancel an active benchmark run
 * Calls Supabase Edge Function to cancel the Cloud Run execution and delete the run record
 * @param {string} runId - Run ID to cancel
 * @returns {Promise<{success: boolean, error: Object}>}
 */
export const cancelRun = async (runId) => {
  try {
    // Get current session for auth token
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error("No active session. Please log in again.");
    }

    // Call Supabase Edge Function to cancel the run
    const supabaseUrl = supabase.supabaseUrl;
    const response = await fetch(
      `${supabaseUrl}/functions/v1/cancel-benchmark`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ runId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();

    return {
      success: true,
      error: null,
      message: data.message || "Run cancelled successfully",
    };
  } catch (error) {
    console.error("Error cancelling run:", error);
    return {
      success: false,
      error: {
        message: error.message || "Failed to cancel run",
      },
    };
  }
};

/**
 * Fetch related runs by their IDs (from related_run_ids array)
 * @param {string[]} relatedRunIds - Array of related run UUIDs
 * @returns {Promise<{data: Array, error: Object}>}
 */
export const fetchRelatedRuns = async (relatedRunIds) => {
  if (!relatedRunIds || relatedRunIds.length === 0) {
    return { data: [], error: null };
  }

  try {
    const { data, error } = await supabase
      .from("bm_runs")
      .select(
        `
        id,
        status,
        inceptbench_version,
        curriculum_version,
        aggregate_score,
        pass_rate,
        created_at,
        completed_at
      `
      )
      .in("id", relatedRunIds)
      .order("created_at", { ascending: true });

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error fetching related runs:", error);
    return { data: null, error };
  }
};

/**
 * Archive a run
 * @param {string} runId - Run ID to archive
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const archiveRun = async (runId) => {
  try {
    const { data, error } = await supabase
      .from("bm_runs")
      .update({
        is_archived: true,
        archived_at: new Date().toISOString(),
      })
      .eq("id", runId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error archiving run:", error);
    return { data: null, error };
  }
};

/**
 * Unarchive a run
 * @param {string} runId - Run ID to unarchive
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const unarchiveRun = async (runId) => {
  try {
    const { data, error } = await supabase
      .from("bm_runs")
      .update({
        is_archived: false,
        archived_at: null,
      })
      .eq("id", runId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error unarchiving run:", error);
    return { data: null, error };
  }
};

/**
 * Update the skip_variety_check flag for a run.
 * @param {string} runId - Run ID
 * @param {boolean} skip - Whether to skip the automatic variety check
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const updateSkipVarietyCheck = async (runId, skip) => {
  try {
    const { data, error } = await supabase
      .from("bm_runs")
      .update({ skip_variety_check: skip })
      .eq("id", runId)
      .select("id, skip_variety_check")
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error updating skip_variety_check:", error);
    return { data: null, error };
  }
};

/**
 * Trigger a variety check for a completed benchmark run.
 * Calls the trigger-variety-check Edge Function which launches a Cloud Run Job.
 * @param {string} runId - Run ID to check variety for
 * @returns {Promise<{success: boolean, error: Object, message: string}>}
 */
export const triggerVarietyCheck = async (runId) => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error("No active session. Please log in again.");
    }

    const supabaseUrl = supabase.supabaseUrl;
    const response = await fetch(
      `${supabaseUrl}/functions/v1/trigger-variety-check`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ runId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();

    return {
      success: true,
      error: null,
      message: "Variety check started successfully",
      data,
    };
  } catch (error) {
    console.error("Error triggering variety check:", error);
    return {
      success: false,
      error: {
        message: error.message || "Failed to trigger variety check",
      },
    };
  }
};
